/**
 * Homly — Navigation config (Phase 1 — Refonte Manager)
 * =====================================================
 * Structure 5 sections + sous-menus pour le rôle ADMIN/MANAGER :
 *   1. Cockpit        — vue d'ensemble, briefing, urgences
 *   2. Mandats        — liste, création, référencement, suivi
 *   3. Vendeurs       — espaces vendeur, visites, offres, satisfaction
 *   4. Équipe         — agents, coaching, KPIs, classement
 *   5. Paramètres     — agence, facturation, intégrations, profil
 *
 * Toutes les pages existantes sont préservées : on les regroupe sous des
 * sections sémantiques. Aucune feature n'est supprimée.
 *
 * Schéma :
 *   { to, icon, label, children?: [...], badge?: 'count|new|beta' }
 */
import {
    LayoutDashboard,
    FileText,
    Users,
    Settings,
    Home,
    PenTool,
    BarChart3,
    Sparkles,
    HelpCircle,
    Euro,
    User,
    MessageSquare,
    TrendingUp,
    BookOpen,
    Target,
    CreditCard,
    Heart,
    Calendar,
    Trophy,
    Shield,
    FolderClosed,
} from 'lucide-react';

// ─── Nav items (sidebar desktop) ─────────────────────────────────────────
export function getNavItems({ isSuperAdmin, isAdmin, isAgent, isSeller, isSoloAgent }) {
    if (isSuperAdmin) {
        return [
            { to: '/super-admin', icon: LayoutDashboard, label: 'Dashboard' },
            { to: '/super-admin/stats', icon: BarChart3, label: 'Statistiques' },
            { to: '/super-admin/email-logs', icon: MessageSquare, label: 'Email logs' },
            { to: '/super-admin/qa', icon: Shield, label: 'QA panel' },
        ];
    }

    if (isAdmin) {
        // 5 sections sémantiques. Chaque section peut être un lien direct
        // OU une rubrique avec sous-menus (children).
        return [
            {
                to: '/admin',
                icon: LayoutDashboard,
                label: 'Cockpit',
                exactMatch: true,
                description: 'Briefing IA, urgences et performances du jour',
            },
            {
                key: 'mandats',
                icon: FileText,
                label: 'Mandats',
                description: 'Pipeline visuel et création de mandats',
                badge: 'mandate-count',
                children: [
                    { to: '/admin/mandates', label: 'Tous les mandats' },
                    { to: '/admin/mandates/upload', label: 'Uploader un mandat', description: 'PDF ou photo · IA Vision' },
                    { to: '/admin/estimations', label: 'Découverte de propriété' },
                    { to: '/admin/offers', label: 'Offres reçues' },
                ],
            },
            {
                key: 'vendeurs',
                icon: Heart,
                label: 'Vendeurs',
                description: 'Espaces vendeurs, visites et satisfaction',
                children: [
                    { to: '/admin/messaging', label: 'Messages', badge: 'unread' },
                    { to: '/admin/reputation', label: 'Satisfaction & avis' },
                ],
            },
            {
                key: 'equipe',
                icon: Users,
                label: 'Équipe',
                description: 'Agents, coaching IA, classements',
                children: [
                    { to: '/admin/team', label: 'Mes agents' },
                    { to: '/admin/agency', label: 'KPIs équipe' },
                    { to: '/admin/leaderboard', label: 'Classement' },
                ],
            },
            {
                key: 'business',
                icon: Euro,
                label: 'Business',
                description: 'Commissions, rémunérations et dossiers notaire',
                children: [
                    { to: '/agence/commissions', label: 'Commissions' },
                    { to: '/agence/commissions/grilles', label: 'Grilles de commission' },
                    { to: '/notaire/dossiers', label: 'Dossiers notaire' },
                ],
            },
            {
                key: 'pilotage',
                icon: BarChart3,
                label: 'Pilotage',
                description: 'Objectifs, KPIs avancés et intelligence IA',
                children: [
                    { to: '/admin/pilotage', label: 'Tableaux dirigeant' },
                    { to: '/admin/targets', label: 'Objectifs CA' },
                    { to: '/admin/ai-insights', label: 'Intelligence IA' },
                    { to: '/admin/kpi', label: 'Catalogue KPI' },
                ],
            },
            {
                key: 'parametres',
                icon: Settings,
                label: 'Paramètres',
                description: 'Agence, facturation et intégrations',
                children: [
                    { to: '/admin/settings', label: 'Mon agence' },
                    { to: '/admin/billing', label: 'Facturation' },
                    { to: '/admin/profile', label: 'Mon profil' },
                ],
            },
        ];
    }

    if (isAgent) {
        const soloLockTooltip = 'Débloqué si votre agence rejoint Homly';
        return [
            { to: '/agent', icon: LayoutDashboard, label: 'Cockpit', exactMatch: true },
            {
                key: 'mandats',
                icon: FileText,
                label: 'Mandats',
                children: [
                    { to: '/agent/mandates', label: 'Mes mandats' },
                    { to: '/agent/mandates/upload', label: 'Uploader un mandat' },
                    { to: '/agent/estimations', label: 'Mes découvertes' },
                    { to: '/agent/offers', label: 'Mes offres' },
                ],
            },
            {
                key: 'vendeurs',
                icon: Heart,
                label: 'Vendeurs',
                children: [
                    { to: '/agent/messaging', label: 'Messages', badge: 'unread' },
                    { to: '/agent/inviter-avis', label: 'Inviter à noter', description: 'Vendeur ou acquéreur' },
                    { to: '/agent/studio-partage', label: 'Studio de partage', description: 'Partagez vos avis sur les réseaux' },
                ],
            },
            {
                key: 'business-agent',
                icon: Euro,
                label: 'Business',
                description: 'Mes commissions et dossiers notaire',
                children: [
                    { to: '/agent/commissions', label: 'Mes commissions' },
                    { to: '/notaire/dossiers', label: 'Dossiers notaire' },
                ],
            },
            // Sections B2B verrouillées pour les agents solo (agence non-abonnée)
            {
                key: 'pilotage-agent',
                icon: BarChart3,
                label: 'Pilotage',
                description: 'KPIs équipe, objectifs et intelligence IA',
                locked: isSoloAgent,
                lockedTooltip: soloLockTooltip,
                children: [
                    { to: '/agent/kpi', label: 'Catalogue KPI' },
                    { to: '/agent/leaderboard', label: 'Classement' },
                ],
            },
            {
                key: 'equipe-agent',
                icon: Users,
                label: 'Équipe',
                description: 'Réputation, certifications et coaching',
                locked: isSoloAgent,
                lockedTooltip: soloLockTooltip,
                children: [
                    { to: '/agent/reputation', label: 'Ma réputation' },
                    { to: '/agent/certifications', label: 'Certifications' },
                ],
            },
            { to: '/agent/profile', icon: User, label: 'Mon profil' },
        ];
    }

    if (isSeller) {
        return [
            { to: '/seller', icon: Home, label: 'Mon espace', exactMatch: true },
            { to: '/seller/messaging', icon: MessageSquare, label: 'Messages', badge: 'unread' },
            { to: '/seller/support', icon: HelpCircle, label: 'Aide & Support' },
        ];
    }

    return [];
}

// ─── Mobile tabs (bottom nav) ────────────────────────────────────────────
export function getMobileTabItems({ isAdmin, isAgent, isSeller }) {
    if (isAdmin) {
        return [
            { to: '/admin', icon: LayoutDashboard, label: 'Cockpit', shortLabel: 'Cockpit' },
            { to: '/admin/mandates', icon: FileText, label: 'Mandats', shortLabel: 'Mandats' },
            { to: '/admin/mandates/upload', icon: PenTool, label: 'Uploader', shortLabel: 'Uploader', isCreate: true },
            { to: '/admin/messaging', icon: MessageSquare, label: 'Messages', shortLabel: 'Messages' },
            { to: '/admin/team', icon: Users, label: 'Équipe', shortLabel: 'Équipe' },
        ];
    }
    if (isAgent) {
        return [
            { to: '/agent', icon: LayoutDashboard, label: 'Cockpit', shortLabel: 'Cockpit' },
            { to: '/agent/mandates', icon: FileText, label: 'Mandats', shortLabel: 'Mandats' },
            { to: '/agent/mandates/upload', icon: PenTool, label: 'Uploader', shortLabel: 'Uploader', isCreate: true },
            { to: '/agent/messaging', icon: MessageSquare, label: 'Messages', shortLabel: 'Messages' },
        ];
    }
    if (isSeller) {
        return [
            { to: '/seller', icon: Home, label: 'Ma vente', shortLabel: 'Ma vente' },
            { to: '/seller/messaging', icon: MessageSquare, label: 'Messages', shortLabel: 'Messages' },
            { to: '/seller/support', icon: HelpCircle, label: 'Aide', shortLabel: 'Aide' },
        ];
    }
    return [];
}

// On exporte des icônes utiles pour les pages qui réutilisent la même
// sémantique visuelle (ex: KPI page, calendrier, profil...).
export const NAV_ICONS = {
    LayoutDashboard, FileText, Users, Settings, Home, PenTool, BarChart3,
    Sparkles, HelpCircle, Euro, User, MessageSquare, TrendingUp, BookOpen,
    Target, CreditCard, Heart, Calendar, Trophy, Shield,
};
